import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ncndaAPI, onboardingAPI } from '@/services/api';

export const ProtectedRoute = ({ allowedRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [gateChecked, setGateChecked] = useState(false);
  const [ncndaRequired, setNcndaRequired] = useState(false);
  const [onboardingRequired, setOnboardingRequired] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || loading || !user) return;
    // Internal users are exempt from NCNDA and onboarding
    if (['super_admin', 'admin', 'internal_team', 'read_only'].includes(user.role)) {
      setGateChecked(true);
      setNcndaRequired(false);
      setOnboardingRequired(false);
      return;
    }
    const check = async () => {
      try {
        const ncndaStatus = await ncndaAPI.getStatus();
        if (ncndaStatus.required) {
          setNcndaRequired(true);
          setOnboardingRequired(false);
          setGateChecked(true);
          return;
        }
        const obStatus = await onboardingAPI.status();
        if (!obStatus.onboardingComplete) {
          setOnboardingRequired(true);
        }
      } catch (err) {
        // If checks fail, let them through
      } finally {
        setGateChecked(true);
      }
    };
    check();
    // Hard fallback — if the gate check hangs for any reason, unblock after 10s
    const timeout = setTimeout(() => setGateChecked(true), 10000);
    return () => clearTimeout(timeout);
  }, [isAuthenticated, loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-teal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-inter text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  // Wait for gate checks to complete for external users
  if (!gateChecked && user && !['super_admin', 'admin', 'internal_team', 'read_only'].includes(user?.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-teal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-inter text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Gate 1: NCNDA
  if (ncndaRequired) {
    return <Navigate to="/portal/ncnda" state={{ returnTo: location.pathname }} replace />;
  }

  // Gate 2: Onboarding (institution selection + 2FA recommendation)
  if (onboardingRequired) {
    return <Navigate to="/portal/onboarding" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
